import { bestFive, bestLowFive } from './snapshotHandRank';
import { CardRef, HandHistorySnapshot, PotGroupPlayerSnapshot } from './types';

/**
 * The five a winning row played, from its hole cards and the board that paid
 * it: the low five for a low award, the best high five otherwise. Undefined
 * when the record cannot say (cards not shown, a board short of a hand).
 */
export const winningFiveFor = (
  player: Pick<
    PotGroupPlayerSnapshot,
    'cards' | 'cardsRevealed' | 'folded' | 'mucked' | 'handType'
  >,
  board: CardRef[],
  variant: 'texas' | 'omaha'
): CardRef[] | undefined => {
  if (!player.cardsRevealed || player.folded || player.mucked || !player.cards?.length) {
    return undefined;
  }
  return player.handType === 'Lo'
    ? bestLowFive(player.cards, board, variant)
    : bestFive(player.cards, board, variant);
};

/**
 * Every winning row with the five it played. The capture persists the engine's
 * five for the board that settled last only (`win{n}` / `lowin{n}` describe one
 * board), so the rows of earlier runs — and any row the record left without a
 * five — are completed here from their own run's board. A persisted five-card
 * row is kept as it is.
 */
export const withWinningCards = (hand: HandHistorySnapshot): HandHistorySnapshot => {
  const boardOf = (run?: number) => (run ? hand.boards?.[run - 1] : undefined) ?? hand.board;
  const fill = (player: PotGroupPlayerSnapshot): PotGroupPlayerSnapshot => {
    if (!player.isWinner || player.winningCards?.length === 5) return player;
    const five = winningFiveFor(player, boardOf(player.run), hand.gameVariant);
    return five ? { ...player, winningCards: five } : player;
  };

  return {
    ...hand,
    potGroups: hand.potGroups.map((pot) => ({ ...pot, players: pot.players.map(fill) })),
    ...(hand.pots
      ? {
          pots: hand.pots.map((pot) => ({
            ...pot,
            awards: pot.awards.map((award) => ({ ...award, players: award.players.map(fill) }))
          }))
        }
      : {})
  };
};
